/*
 * The membership page.
 *
 * An asset rather than a script inside the section, for the reason the Patron
 * and plans pages are: the theme inserts swapped-in markup without re-executing
 * a section's inline script, so arriving here by a link left the page inert.
 *
 * Here that was worse than cosmetic. Cancelling writes nothing to the customer
 * record — only the contract knows — so Liquid always renders this page as
 * though the membership were live, and it is this script that asks and rewrites
 * it. Reached through a transition, a member who had cancelled was shown their
 * tier as active, with Change and Cancel offered again. That is how somebody
 * cancels twice.
 */
(function () {
  "use strict";

  function init() {
    var root = document.querySelector('[data-od-manage]');
    if (!root) return false;
    if (root.dataset.odBound === '1') return true;
    root.dataset.odBound = '1';

    var CHANGE_URL = '/apps/library/membership/change-tier';
    var CANCEL_URL = '/apps/library/membership/cancel';

    var result = root.querySelector('[data-od-result]');

    /* The shop caches the customer's tier so every page does not have to ask
       for it. Changing or ending a membership makes that cache wrong, and a
       stale one shows a discount the cart will not honour. */
    function forgetMemberTier(nextTier) {
      try {
        sessionStorage.removeItem('od-member-tier-v1');
        sessionStorage.removeItem('od-active-sale-v3');
      } catch (error) {
        /* private mode: nothing was cached to begin with */
      }

      /* Anything on the page that gates on membership hears about it now
         rather than after the reload. The Coming Soon gate is the one that
         matters: somebody who has just upgraded to Patron must not be offered
         Patron. Detail is optional, and a listener with no tier re-asks. */
      try {
        window.dispatchEvent(
          new CustomEvent('od:membership-changed', {
            detail: typeof nextTier === 'string' ? { tier: nextTier } : {}
          })
        );
      } catch (error) {
        /* CustomEvent is available everywhere we support; never worth
           failing a completed change over. */
      }
    }

    /* Telling somebody to add a card is only half the job if they then have to
       go and find the page themselves. The link is passed in rather than built
       here, because only the merchant knows where the account lives. */
    function show(kind, message, link) {
      if (!result) return;
      result.hidden = false;
      result.className = 'od-manage__result od-manage__result--' + kind;
      result.textContent = message;

      if (link && link.href) {
        result.appendChild(document.createTextNode(' '));
        var anchor = document.createElement('a');
        anchor.className = 'od-manage__textlink';
        anchor.href = link.href;
        anchor.textContent = link.label || 'Add a card';
        result.appendChild(anchor);
      }

      result.setAttribute('role', kind === 'error' ? 'alert' : 'status');
      result.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* Where a member goes to put a card on the account. Empty when the setting
       has not been filled in, and then nothing is linked rather than a link to
       nowhere being offered. */
    var CARD_URL = (root.getAttribute('data-od-card-url') || '').trim();

    function cardLink(data) {
      if (!CARD_URL) return null;
      if (!data || data.code !== 'no_payment_method') return null;
      return { href: CARD_URL, label: 'Add a card to your account' };
    }

    // Every button is disabled during a request, not just the one pressed. A
    // second change committed while the first is still in flight would race,
    // and the page's idea of the current tier is already stale by then.
    function setBusy(busy, pressed, label) {
      var buttons = root.querySelectorAll('button');
      for (var i = 0; i < buttons.length; i++) {
        var button = buttons[i];
        if (busy) {
          if (!button.disabled) button.dataset.odWasEnabled = '1';
          button.disabled = true;
        } else if (button.dataset.odWasEnabled === '1') {
          button.disabled = false;
          delete button.dataset.odWasEnabled;
        }
      }
      if (pressed) pressed.textContent = busy ? (label || 'Working...') : pressed.dataset.odLabel;
    }

    function post(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body || {})
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          return { status: response.status, data: data };
        });
      });
    }

    /* ---- status first, options behind a press ------------------------- */
    var disclosure = root.querySelector('[data-od-disclosure]');
    var optionsPanel = root.querySelector('[data-od-options]');

    function openOptions() {
      if (!optionsPanel || !disclosure) return;
      optionsPanel.hidden = false;
      /* Read a layout property first, so the browser treats what follows as a
         change to animate rather than as the panel's starting state. */
      void optionsPanel.offsetHeight;
      optionsPanel.classList.add('is-open');
      disclosure.setAttribute('aria-expanded', 'true');
    }

    function closeOptions() {
      if (!optionsPanel || !disclosure) return;
      optionsPanel.classList.remove('is-open');
      disclosure.setAttribute('aria-expanded', 'false');
      window.setTimeout(function () {
        if (!optionsPanel.classList.contains('is-open')) optionsPanel.hidden = true;
      }, 320);
    }

    if (disclosure) {
      disclosure.addEventListener('click', function () {
        if (disclosure.getAttribute('aria-expanded') === 'true') closeOptions();
        else openOptions();
      });
    }

    /* ---- confirming a change ------------------------------------------ */
    /*
     * In the section on a first render, at the body once this has run. A page
     * that binds twice — a swap that keeps the section, a second pass from the
     * observer — would otherwise look only inside the section, find nothing,
     * and fall through to the plain browser prompt.
     */
    var confirmBox =
      root.querySelector('[data-od-confirm]') || document.querySelector('[data-od-confirm]');

    /*
     * Out to the body, exactly as the Patron page's dialog is.
     *
     * <main> carries a transform, and a transformed ancestor becomes the
     * containing block for position: fixed — so this dialog centred itself
     * inside the section rather than the viewport and opened somewhere down
     * the page, which reads as nothing having happened at all.
     *
     * A stale copy from a previous visit is removed first: the theme swaps the
     * page without unloading it, so one left at the body would outlive the
     * section it belongs to.
     */
    if (confirmBox) {
      if (confirmBox.id) {
        var staleConfirm = document.getElementById(confirmBox.id);
        if (staleConfirm && staleConfirm !== confirmBox && staleConfirm.parentNode === document.body) {
          staleConfirm.parentNode.removeChild(staleConfirm);
        }
      }
      document.body.appendChild(confirmBox);
    }
    /*
     * From the dialog, not from the section.
     *
     * The dialog is moved out to the body above, so by this point it is no
     * longer inside root and these four came back null — and askToConfirm
     * treats a missing dialog as "nothing to ask", running the change at once.
     * Which is what it did: the tier changed, the letter went, and no question
     * was ever put.
     */
    var confirmTitle = confirmBox && confirmBox.querySelector('[data-od-confirm-title]');
    var confirmBody = confirmBox && confirmBox.querySelector('[data-od-confirm-body]');
    var confirmFacts = confirmBox && confirmBox.querySelector('[data-od-confirm-facts]');
    var confirmGo = confirmBox && confirmBox.querySelector('[data-od-confirm-go]');
    var pendingConfirm = null;

    function closeConfirm() {
      if (!confirmBox || confirmBox.hidden) return;
      confirmBox.classList.remove('is-open');
      pendingConfirm = null;
      window.setTimeout(function () {
        if (!confirmBox.classList.contains('is-open')) confirmBox.hidden = true;
      }, 200);
    }

    /*
     * Worded by direction, because the two changes frighten people in opposite
     * ways. An upgrade is immediate and one way, so it states what it costs and
     * that they can move back. A downgrade leads with what they keep and until
     * when: the usual fear is losing access the moment they press it.
     */
    function askToConfirm(options) {
      if (!confirmBox || !confirmTitle || !confirmBody || !confirmFacts || !confirmGo) {
        /*
         * Ask anyway.
         *
         * This used to run the change without a question when the dialog could
         * not be found, and that is exactly what happened: the dialog was moved
         * to the body while these were still being looked for in the section,
         * so a press on "Change to Member" changed the membership and sent the
         * letter with nothing asked. A plain browser prompt is a poor dialog
         * and a far better failure — whatever is wrong with the page, nobody's
         * membership should change because they clicked once.
         */
        var asked = options.title
          ? options.title
          : (options.isUpgrade ? 'Become a ' : 'Change to ') + options.name + '?';

        if (window.confirm(asked)) options.onConfirm();
        return;
      }

      pendingConfirm = options.onConfirm;

      var facts = [];

      if (options.title) {
        /* Worded by the caller. Cancelling is not a tier change and the two
           sentences that matter for it are not the ones below. */
        confirmTitle.textContent = options.title;
        confirmBody.textContent = options.body || '';
        facts = options.facts || [];
        confirmGo.textContent = options.cta || 'Yes';
      } else if (options.isUpgrade) {
        confirmTitle.textContent = 'Become a ' + options.name + '?';
        confirmBody.textContent =
          'Everything a ' + options.name + ' receives becomes available to you straight away.';
        facts.push('You are not charged anything today.');
        if (options.price) facts.push('Your next payment will be ' + options.price + '.');
        facts.push('You can move back down at any time.');
        confirmGo.textContent = 'Yes, become a ' + options.name;
      } else {
        confirmTitle.textContent = 'Change to ' + options.name + '?';
        confirmBody.textContent =
          'Nothing changes today. You keep everything your current membership includes until the end of the period you have already paid for.';
        facts.push('From then you will be a ' + options.name + '.');
        if (options.price) facts.push('Your new rate will be ' + options.price + '.');
        facts.push('You can change your mind before then.');
        confirmGo.textContent = 'Yes, change to ' + options.name;
      }

      confirmFacts.innerHTML = '';
      for (var f = 0; f < facts.length; f++) {
        var li = document.createElement('li');
        li.textContent = facts[f];
        confirmFacts.appendChild(li);
      }

      confirmBox.hidden = false;
      void confirmBox.offsetWidth;
      confirmBox.classList.add('is-open');
      confirmGo.focus();
    }

    if (confirmBox) {
      confirmBox.addEventListener('click', function (event) {
        if (event.target.hasAttribute('data-od-confirm-cancel')) closeConfirm();
      });

      if (confirmGo) {
        confirmGo.addEventListener('click', function () {
          var run = pendingConfirm;
          closeConfirm();
          if (run) run();
        });
      }

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeConfirm();
      });
    }

    var changeButtons = root.querySelectorAll('[data-od-change]');

    for (var i = 0; i < changeButtons.length; i++) {
      (function (button) {
        button.dataset.odLabel = button.textContent.trim();

        button.addEventListener('click', function () {
          var tier = button.dataset.tier;
          var name = button.dataset.tierName || tier;
          var isUpgrade = button.dataset.relation === 'upgrade';

          /* Nothing is sent until they confirm. */
          askToConfirm({
            isUpgrade: isUpgrade,
            name: name,
            price: button.dataset.price || '',
            onConfirm: function () { commit(); }
          });

          function commit() {
          setBusy(true, button, isUpgrade ? 'Upgrading...' : 'Changing...');

          post(CHANGE_URL, { tier: tier }).then(function (res) {
            if (res.status === 401) {
              setBusy(false, button);
              show('error', 'Please sign in again to change your membership.');
              return;
            }

            if (!res.data || res.data.ok !== true) {
              setBusy(false, button);
              show(
                'error',
                (res.data && res.data.error) || 'We could not change your membership just now. Please try again, or write to us at info@operadepot.com.',
                cardLink(res.data)
              );
              return;
            }

            forgetMemberTier(
              res.data.appliedImmediately ? String(res.data.toTier || '') : undefined
            );

            if (res.data.appliedImmediately) {
              /* The summary is the first thing they look at, and a success
                 message beside a stale tier reads as though nothing took. */
              var tierEl = root.querySelector('.od-manage__status-tier');
              if (tierEl) tierEl.textContent = name;

              show('ok', 'You are now a ' + name + ' member. Your new benefits are available right away, and your next renewal will be at the new price.');
            } else {
              show('ok', 'Your membership will change to ' + name + ' at your next renewal. You keep your current benefits until then.');
            }

            /*
             * The page was rendered from membership state that has now moved.
             * Re-rendering by hand would mean duplicating the tier logic in
             * JavaScript and getting to disagree with Liquid about it.
             *
             * The reload now waits for the dialog to be closed. A downgrade
             * leaves the old tier on screen — correctly, because that is what
             * they still have — and reloading out from under the explanation
             * takes away the only thing saying so.
             */
            var reload = function () { window.location.reload(); };

            if (window.ODMembershipDone) {
              window.ODMembershipDone.show(res.data, { root: root, onClose: reload });
            } else {
              window.setTimeout(reload, 2600);
            }
          }).catch(function () {
            setBusy(false, button);
            show('error', 'Something went wrong reaching us. Please check your connection and try again.');
          });
          }
        });
      })(changeButtons[i]);
    }

    /* Whether cancelling right now would refund them. The page has no idea
       when the member joined, so it has to ask. */
    var guaranteeApplies = false;
    var confirmText = root.querySelector('[data-od-confirm-text]');

    /*
     * Says what has happened and takes away the option to do it again.
     *
     * The cancellation is the only fact somebody wants at this point, and the
     * date it takes effect is the second. Offering Cancel again to a member who
     * has already cancelled is what produced a second cancellation.
     */
    /*
     * "On October 4 your membership becomes Member."
     *
     * A downgrade changes the contract at once and leaves the entitlements
     * until the paid period ends, so this page keeps showing the tier they
     * still hold — which is right, and silent about what is coming. The
     * contract is the only thing that knows, so the tier it will bill next
     * comes back with the guarantee answer and is compared with the tier
     * Liquid rendered.
     *
     * An upgrade needs nothing here: it takes effect immediately, so the two
     * tiers already agree by the time this page is drawn.
     */
    function showPendingChange(membership) {
      var note = root.querySelector('[data-od-pending-change]');
      if (!note || !membership) return;

      var billsAs = String(membership.billsAsTier || '').toLowerCase();
      var holding = String(root.dataset.currentTier || '').toLowerCase();
      var when = membership.nextBillingDate || membership.accessEndsAt;

      if (!billsAs || !holding || billsAs === holding || !when) return;

      var on = new Date(when);
      if (isNaN(on.getTime())) return;

      var named = billsAs.charAt(0).toUpperCase() + billsAs.slice(1);
      var day = on.toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric'
      });

      note.textContent = 'On ' + day + ' your membership becomes ' + named + '.';
      note.hidden = false;
    }

    function showCancelled(accessEndsAt, keepResult) {
      var statusTier = root.querySelector('.od-manage__status-tier');
      var statusMeta = root.querySelector('.od-manage__status-meta');

      var when = '';
      /* A date already gone is treated as no date. A contract whose charge
         failed keeps its billing date while the retries run, so somebody who
         cancels during dunning has one in the past, and "your access continues
         through 7 September" on the 19th reads as a mistake or a loss. Both
         messages below already word themselves without a date. */
      if (accessEndsAt && Date.parse(accessEndsAt) > Date.now()) {
        var parsed = new Date(accessEndsAt);
        if (!isNaN(parsed.getTime())) {
          when = parsed.toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
          });
        }
      }

      if (statusTier) {
        statusTier.textContent =
          (statusTier.textContent || '').trim() + ' (cancelled)';
      }

      if (statusMeta) {
        statusMeta.innerHTML = '';
        var line = document.createElement('p');
        line.textContent = when
          ? 'Your membership is cancelled. Your access continues through ' + when + '.'
          : 'Your membership is cancelled. Your access continues to the end of the period you have paid for.';
        statusMeta.appendChild(line);
      }

      /* Nothing left to change or to cancel. The way back is joining again,
         which is what the disclosure now offers.

         keepResult is for the moment just after cancelling: the confirmation
         message lives inside this box, so hiding the whole thing would take
         away the sentence saying it worked. The controls go, the message
         stays. */
      if (keepResult) {
        var parts = root.querySelectorAll(
          '.od-manage__options, .od-manage__cancel, [data-od-disclosure]'
        );
        for (var p = 0; p < parts.length; p++) parts[p].hidden = true;
      } else {
        var manageBox = root.querySelector('.od-manage__manage');
        if (manageBox) manageBox.hidden = true;
      }

      var notice = document.createElement('p');
      notice.className = 'od-manage__footnote';
      notice.innerHTML =
        'Changed your mind? <a class="od-manage__textlink" href="' +
        (root.dataset.odJoinUrl || '/pages/membership') +
        '" x-element-transition-trigger>Start a membership again</a>.';

      var inner = root.querySelector('.od-manage__inner');
      if (inner) inner.appendChild(notice);
    }

    /*
     * Wait before showing the two things that are about to change.
     *
     * Liquid cannot know a membership is cancelled — only the contract does —
     * so it always renders this page as though it were live, and the call below
     * rewrites it a moment later. Somebody who has cancelled watched "renews on
     * 16 October" and a Cancel button appear and then be taken away.
     *
     * The class is added by script rather than written into the markup, so a
     * page whose JavaScript never runs hides nothing. It is visibility rather
     * than display, so the space is held and nothing moves when the answer
     * arrives.
     */
    root.classList.add('is-checking');

    var settled = false;

    function settle() {
      if (settled) return;
      settled = true;
      root.classList.remove('is-checking');
    }

    /* Never hidden for long, whatever happens to the request. A member who
       cannot reach us must still be able to cancel. */
    window.setTimeout(settle, 4000);

    fetch('/apps/library/membership/guarantee', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (!data) return;

        /*
         * A membership already on its way out.
         *
         * Cancelling stops billing and leaves access running to the end of the
         * paid period, and it writes nothing to the customer record, so this
         * page looked exactly as it had before: same entitlements, same Cancel
         * button. Somebody reasonably concludes it did not work and cancels a
         * second time.
         *
         * Liquid cannot see this. The contract is the only thing that knows,
         * and this is the call the page already makes.
         */
        if (data.membership && data.membership.status === 'cancelled') {
          showCancelled(data.membership.accessEndsAt);
          return;
        }

        showPendingChange(data.membership);

        if (data.eligible !== true) return;

        guaranteeApplies = true;

        if (confirmText) {
          confirmText.textContent =
            'You are still within your first ' + (data.days || 14) +
            ' days, so we will refund you in full. Your access will end today.';
        }
      })
      .catch(function () {
        /* Leave the ordinary wording. Promising a refund we could not confirm
           would be worse than not mentioning one. */
      })
      /* Answered or not, the page stops waiting. */
      .then(settle, settle);

    var cancelOpen = root.querySelector('[data-od-cancel-open]');
    var cancelBox = root.querySelector('[data-od-cancel-confirm]');
    var cancelClose = root.querySelector('[data-od-cancel-close]');
    var cancelConfirm = root.querySelector('[data-od-cancel-confirmed]');

    if (cancelOpen && cancelBox) {
      cancelOpen.addEventListener('click', function () {
        cancelBox.hidden = false;
        cancelOpen.hidden = true;
        if (cancelConfirm) cancelConfirm.focus();
      });
    }

    if (cancelClose && cancelBox && cancelOpen) {
      cancelClose.addEventListener('click', function () {
        cancelBox.hidden = true;
        cancelOpen.hidden = false;
        cancelOpen.focus();
      });
    }

    if (cancelConfirm) {
      cancelConfirm.dataset.odLabel = cancelConfirm.textContent.trim();

      cancelConfirm.addEventListener('click', function () {
        /*
         * The last gate. The box this button sits in explains what happens;
         * this one asks whether they meant it, because cancelling cannot be
         * undone — a cancelled contract is finished, and coming back means a
         * new membership at whatever it costs then.
         */
        askToConfirm({
          title: 'Cancel your membership?',
          body: 'This cannot be undone. If you come back later it will be a new membership, at the price of the day.',
          facts: [
            'Your access continues to the end of the period you have paid for.',
            'You will not be charged again.',
            'Anything you have purchased stays yours.'
          ],
          cta: 'Yes, cancel my membership',
          onConfirm: function () { cancelNow(); }
        });
      });

      function cancelNow() {
        setBusy(true, cancelConfirm, 'Cancelling...');

        post(CANCEL_URL, {}).then(function (res) {
          if (!res.data || res.data.ok !== true) {
            setBusy(false, cancelConfirm);
            show('error', (res.data && res.data.error) || 'We could not cancel your membership just now. Please write to us at info@operadepot.com and we will take care of it.');
            return;
          }

          forgetMemberTier();

          if (cancelBox) cancelBox.hidden = true;

          /* Every control goes now, not after the reload. Leaving Cancel on the
             page beside "your membership has been cancelled" is what produced a
             second cancellation. */
          showCancelled(res.data.accessEndsAt || null, true);

          /* A refunded member has no access left to describe. The old fallback
             said "continues to the end of your current billing period" whenever
             there was no date, which is precisely the refund case, so the one
             person who had just been cut off was told the opposite. */
          var when = res.data.accessEndsAtLabel;

          if (res.data.refunded) {
            show('ok',
              'Your membership has been cancelled and we have refunded ' +
              (res.data.refundAmount ? '$' + res.data.refundAmount : 'your payment') +
              ' in full. Your access has ended, and you will not be charged again. ' +
              'We have sent you a confirmation by email.');
          } else {
            show('ok', when
              ? 'Your membership has been cancelled. Your access continues through ' + when + ', and you will not be charged again. We have sent you a confirmation by email.'
              : 'Your membership has been cancelled. Your access continues to the end of your current billing period, and you will not be charged again. We have sent you a confirmation by email.');
          }

          /* Longer for a refund: it is more to take in, and the reload replaces
             it with "you do not have an active membership", which reads as a
             non sequitur if it arrives too quickly. */
          window.setTimeout(
            function () { window.location.reload(); },
            res.data.refunded ? 9000 : 4000
          );
        }).catch(function () {
          setBusy(false, cancelConfirm);
          show('error', 'Something went wrong reaching us. Please check your connection and try again.');
        });
      }
    }
    return true;
  }

  /*
   * Watching the document, not counting the seconds. The theme dispatches
   * pushstate when the link is pressed, before it has fetched the new page, and
   * od:after-swap does not fire on every route, so a window of retries expires
   * while the old page is still on screen. An observer has no window to get
   * wrong, and is what od-sale-display.js does.
   */
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;

    /*
     * A frame or a timer, whichever comes first.
     *
     * requestAnimationFrame does not fire in a background tab, and the flag
     * above latches while one is pending, so a page opened in a tab nobody is
     * looking at never initialised: the billing toggle dead, the cart forms
     * unclaimed, and the refund promise on screen without having been checked.
     * It recovered when the tab was focused, which is late.
     */
    var run = function () {
      if (!scheduled) return;
      scheduled = false;
      init();
    };

    window.requestAnimationFrame(run);
    window.setTimeout(run, 50);
  }

  schedule();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener("od:after-swap", schedule);
  window.addEventListener("pushstate", schedule);
  window.addEventListener("popstate", schedule);
})();
